import { Component, Input, OnChanges, TemplateRef } from "@angular/core";
import { Zone, ZoneMetadata } from "@common/ADAPT.Common.Model/methodology/zone";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { OrganisationMapService } from "@org-common/lib/architecture/organisation-map/organisation-map.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { OrganisationMapFilterService } from "app/organisation/organisation-map-filter/organisation-map-filter.service";
import { combineLatest, Observable, ReplaySubject } from "rxjs";
import { distinctUntilChanged, map, switchMap } from "rxjs/operators";
import { Options as SortablejsOptions } from "sortablejs";

@Component({
    selector: "adapt-list-entities-in-location",
    templateUrl: "./list-entities-in-location.component.html",
    styleUrls: ["./list-entities-in-location.component.scss"],
    standalone: false,
})
export class ListEntitiesInLocationComponent extends BaseComponent implements OnChanges {
    public readonly ZoneName = ZoneMetadata.Name;

    @Input({ required: true }) public zone!: Zone;
    @Input() public teamTemplate?: TemplateRef<unknown>;
    @Input() public filterTeams?: (Team: Team[]) => Team[] | Observable<Team[]>;
    @Input() public isEditing = false;

    public locationItems$: Observable<TeamLocation[]>;
    private location$ = new ReplaySubject<Partial<TeamLocation>>(1);

    public draggingTeamCanBeDroppedIntoZone = true;

    public sortableOptions: SortablejsOptions = {
        ...this.orgMapService.defaultSortableOptions,
    };

    public constructor(
        private commonTeamsService: CommonTeamsService,
        private orgMapService: OrganisationMapService,
        organisationMapFilterService: OrganisationMapFilterService,
    ) {
        super();

        this.locationItems$ = combineLatest([
            this.location$.pipe(
                switchMap((loc) => this.orgMapService.emitWhenLocationShouldBeFetched(loc)),
            ),
            organisationMapFilterService.filter$.pipe(
                map((filterParams) => filterParams.showEndedTeams),
                distinctUntilChanged(),
            ),
        ]).pipe(
            switchMap(([loc, showEndedTeams]) => this.orgMapService.getLocationItems(loc, showEndedTeams)),
            switchMap((locationItems) => this.applyTeamFilter(locationItems)),
        );

        this.orgMapService.mapEntityDragging$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.draggingTeamCanBeDroppedIntoZone = this.orgMapService.draggingTeamCanBeDroppedIntoZone(this.zone);
        });
    }

    public ngOnChanges() {
        this.location$.next({ zone: this.zone });

        this.sortableOptions = { ...this.sortableOptions, disabled: !this.isEditing };
    }

    private applyTeamFilter(locationItems: TeamLocation[]) {
        const teams = locationItems.map((i: TeamLocation) => i.team);
        return this.commonTeamsService.applyTeamsFilter(teams, this.filterTeams).pipe(
            map((showTeams) => locationItems.filter((teamLocation) => teamLocation
                ? showTeams.includes(teamLocation.team)
                : true)),
        );
    }
}
